import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { Loading } from "../../../../components/Loading";
import Select from "../../../../components/input/Select";
import { SimpleOption } from "../../../../components/input/SimpleListbox";
import { useGetGenresQuery, useGetScoreCategoriesQuery } from "../../../lookup/lookupApiSlice";
import { getReportLevel, buildReportQueryString, reportSubLevelName } from "../adminReportsUtil";
import {
	ReportLevel,
	AdminReportsResponse,
	useGetDraftTypesForReportsQuery,
	useGetSchoolYearsForReportsQuery,
} from "../adminReportsApiSlice";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import {
	AverageMonthlyScoresRow,
	useLazyAverageMonthlyScoresQuery,
} from "./adminProgressReportApiSlice";
import { AverageMonthlyScoresChart } from "./AverageMonthlyScoresChart";
import { AverageMonthlyScoresTable } from "./AverageMonthlyScoresTable";

export default function AverageMonthlyScores() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [data, setData] = useState<AdminReportsResponse<AverageMonthlyScoresRow>>();
	const [lastUpdated, setLastUpdated] = useState<Date>();
	const [hideRows, setHideRows] = useState(false);
	const [reportLevel, setReportLevel] = useState<ReportLevel>(maxReportLevel);

	const [schoolYearOptions, setSchoolYearOptions] = useState<SimpleOption[]>([]);
	const [traitOptions, setTraitOptions] = useState<SimpleOption[]>([]);
	const [genreOptions, setGenreOptions] = useState<SimpleOption[]>([]);
	const [draftTypeOptions, setDraftTypeOptions] = useState<SimpleOption[]>([]);

	const [yearFilter, setYearFilter] = useState<string>("");
	const [draftTypeFilter, setDraftTypeFilter] = useState(0);
	const [genreFilter, setGenreFilter] = useState(0);
	const [traitFilter, setTraitFilter] = useState(0);

	const traitsResult = useGetScoreCategoriesQuery();
	const traits = traitsResult.data;

	const genresResult = useGetGenresQuery();
	const genres = genresResult.data;

	const getSchoolYearsResult = useGetSchoolYearsForReportsQuery(undefined);
	const schoolYears = getSchoolYearsResult.data;

	const getDraftTypesResult = useGetDraftTypesForReportsQuery(undefined);
	const draftTypes = getDraftTypesResult.data;

	const [callAverageScores] = useLazyAverageMonthlyScoresQuery();

	useEffect(() => {
		if (schoolYears) {
			const options = schoolYears.map(
				(d) => ({ value: d.value, label: d.text }) as SimpleOption
			);

			setSchoolYearOptions(options);
			setYearFilter(options[0].value as string);
		}
	}, [schoolYears]);

	useEffect(() => {
		if (draftTypes) {
			const options = draftTypes.map(
				(d) =>
					({
						value: d.value == "null" ? 0 : Number(d.value),
						label: d.text,
					}) as SimpleOption
			);

			setDraftTypeOptions(options);
		}
	}, [draftTypes]);

	useEffect(() => {
		if (traits) {
			const options = traits
				.filter((t) => t.isEssayTrait)
				.map(
					(t) =>
						({
							value: t.scoreCategoryId,
							label: t.name,
						}) as SimpleOption
				);
			options.unshift({ value: 0, label: "Total Score" });
			setTraitOptions(options);
		}
	}, [traits]);

	useEffect(() => {
		if (genres) {
			const options = genres
				.filter((t) => t.isEssayGenre)
				.map(
					(t) =>
						({
							value: t.genreId,
							label: t.name,
						}) as SimpleOption
				);
			options.unshift({ value: 0, label: "All Genres" });
			setGenreOptions(options);
		}
	}, [genres]);

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent auto-fail calls
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		if (yearFilter) {
			callAverageScores(
				{
					districtId: districtFilter === 0 ? undefined : districtFilter,
					schoolId: schoolFilter === 0 ? undefined : schoolFilter,
					schoolYear: Number(yearFilter),
					draftStatus: draftTypeFilter === 0 ? undefined : draftTypeFilter,
					genreId: genreFilter === 0 ? undefined : genreFilter,
					scoreCategoryId: traitFilter === 0 ? undefined : traitFilter,
				},
				true //cache the results
			)
				.unwrap()
				.then((payload) => {
					setData(payload.response);
					setLastUpdated(payload.lastUpdateDate);
					setReportLevel(getReportLevel(districtFilter, schoolFilter));
				})
				.catch((error) => {
					console.log(error.data);
					navigate("/adminReports");
				});
		}
	}, [districtFilter, schoolFilter, yearFilter, draftTypeFilter, genreFilter, traitFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
			yearFilter,
			draftTypeFilter,
			genreFilter,
			traitFilter,
		});
	}, [districtFilter, schoolFilter, yearFilter, draftTypeFilter, genreFilter, traitFilter]);

	return (
		<section>
			<h1>
				{reportLevel} Average Monthly Scores by {reportSubLevelName(reportLevel)}
			</h1>
			<p className="mt-4 print:hidden">
				This report displays the average total scores by month. Use the filters below{" "}
				{reportLevel !== ReportLevel.School ? "or select the name in the table" : ""} to
				view data for that selection. Click the three dots to print or download.
			</p>
			<div className="mt-3">
				<Loading loading={!data} />
				{data && (
					<>
						<div className="flex justify-between items-end gap-x-3">
							<div className="print:hidden">
								<DistrictSchoolFilters
									districtOptions={districtOptions}
									schoolOptions={schoolOptions}
									districtFilter={districtFilter}
									schoolFilter={schoolFilter}
									setDistrictFilter={setDistrictFilter}
									setSchoolFilter={setSchoolFilter}
								/>
								<div className="flex flex-wrap gap-3 items-end mt-2">
									<Select
										value={yearFilter}
										setValue={(v) => setYearFilter(v as string)}
										label="School Year"
										simpleListBoxId="yearFilter"
										options={schoolYearOptions}
										fieldClasses="w-full md:w-auto"
										simpleListBoxClasses="w-56"
									/>
									<Select
										value={traitFilter}
										setValue={(v) => setTraitFilter(v as number)}
										label="Category"
										simpleListBoxId="traitFilter"
										options={traitOptions}
										fieldClasses="w-full md:w-auto"
										simpleListBoxClasses="w-56"
									/>
									<Select
										value={genreFilter}
										setValue={(v) => setGenreFilter(v as number)}
										label="Genre"
										simpleListBoxId="genreFilter"
										options={genreOptions}
										fieldClasses="w-full md:w-auto"
										simpleListBoxClasses="w-56"
									/>
									<Select
										value={draftTypeFilter}
										setValue={(v) => setDraftTypeFilter(v as number)}
										label="Draft Type"
										simpleListBoxId="draftFilter"
										options={draftTypeOptions}
										fieldClasses="w-full md:w-auto"
										simpleListBoxClasses="w-56"
									/>
								</div>
							</div>

							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={data.rows.length !== 0}
								hideRows={hideRows}
								setHideRows={setHideRows}
								uniqueKey="average-scores"
								downloadExcelLink={`${aspUrl}/AdminReports/AverageMonthlyScoresExcel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/AverageMonthlyScoresCsv?${queryString}`}
							/>
						</div>

						<AverageMonthlyScoresTable
							className="mt-4"
							level={reportLevel}
							data={data}
							hideRows={hideRows}
							setDistrictFilter={setDistrictFilter}
							setSchoolFilter={setSchoolFilter}
						/>
						<div className="print:hidden">
							<AverageMonthlyScoresChart
								level={reportLevel}
								trait={traitFilter ?? 0}
								totals={data.totals}
								hasData={data.rows.length > 0}
							/>
						</div>
					</>
				)}
			</div>
		</section>
	);
}
